import { useAuthData, useUserRoles } from '@anytag/auth';
import { useAdvertiserForAddEngagementQuery, useCreateEngagementMutation } from '@anytag/graphql';
import { RenderDataWithFallback } from '@anytag/shared-atoms';
import { UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { useQueryHelper, useUpdateDocumentTitle } from '@anytag/shared-hooks';
import { Header } from '@anytag/shared-molecules';
import {
  EngagementCampaignForm,
  EngagementCampaignFormValues,
  formatEngagementFormVariables,
  defaultEngagementCampaignFormValues
} from '@anytag/shared-organisms';
import { FormAction } from '@anytag/shared-types';

export const AddEngagement = () => {
  useUpdateDocumentTitle({
    href: '/engagement',
    title: 'documentTitle.AddEngagement',
    appHeader: 'appHeader.Add New Engagement'
  });
  const { auth } = useAuthData();
  const { isAdvertiser, isAgency } = useUserRoles();
  const { t, navigate, enqueueSnackbar } = useQueryHelper();
  const { callCreateEngagement } = useCreateEngagementMutation();

  const { data, loading } = useAdvertiserForAddEngagementQuery({
    skip: !isAdvertiser,
    variables: { pk: auth.userId },
    fetchPolicy: 'cache-and-network'
  });

  const handleSubmit: FormAction<EngagementCampaignFormValues>['onSubmit'] = async (values) => {
    const input = formatEngagementFormVariables(values);
    const { budget, socialMedias, advertiserId, campaignCategoryId } = input;

    if (!advertiserId || !budget || socialMedias.length === 0 || !campaignCategoryId) {
      return;
    }

    try {
      const { data } = await callCreateEngagement({ variables: { input } });
      const createdId = data?.createEngagement?.id;
      enqueueSnackbar(t('succeededInCreating', { name: values.title }), { variant: 'success' });

      if (!createdId) {
        navigate({ to: '/engagement' });
      } else if (!isAdvertiser && !isAgency) {
        navigate({ to: '/engagement/$id/influencer', params: { id: createdId.toString() } });
      }
    } catch (error) {
      enqueueSnackbar(t('failedToCreate', { name: values.title }), { variant: 'error' });
      enqueueSnackbar(t(error || UNEXPECTED_ERROR), { variant: 'error' });
    }
  };

  return (
    <RenderDataWithFallback loading={loading}>
      <>
        <Header />
        <EngagementCampaignForm
          onSubmit={handleSubmit}
          defaultValues={{
            ...defaultEngagementCampaignFormValues,
            advertiserId:
              data?.advertiserForAdvertiserUser?.id.toString() || defaultEngagementCampaignFormValues.advertiserId
          }}
        />
      </>
    </RenderDataWithFallback>
  );
};
